import { lazy, Suspense, ComponentType } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { CreditsProvider } from "./contexts/CreditsContext";
import { VideoPlaybackProvider } from "./contexts/VideoPlaybackContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AdminRoute } from "./components/AdminRoute";
import { ScrollToTop } from "./components/ScrollToTop";
import { MetaPixelTracker } from "./components/MetaPixelTracker";
import { ChunkErrorBoundary } from "./components/ChunkErrorBoundary";

// Eager load - needed for initial page
import LandingPage from "./pages/LandingPage";
import Auth from "./pages/Auth";
import Gallery from "./pages/Gallery";

// Função para lazy load com retry automático em caso de erro de chunk
const lazyWithRetry = (
  componentImport: () => Promise<{ default: ComponentType<unknown> }>
) => {
  return lazy(async () => {
    try {
      return await componentImport();
    } catch (error) {
      // Se falhar, verifica se é erro de chunk e força reload
      if (
        error instanceof Error &&
        (error.message.includes("Failed to fetch dynamically imported module") ||
          error.message.includes("error loading dynamically imported module") ||
          error.message.includes("Loading chunk"))
      ) {
        // Limpa cache e recarrega a página
        window.location.reload();
        // Retorna promessa que nunca resolve para evitar erro
        return new Promise(() => {});
      }
      throw error;
    }
  });
};

// Lazy load com retry - não necessários no carregamento inicial
const GeneratorPage = lazyWithRetry(() => import("./pages/GeneratorPage"));
const VideoGeneratorPage = lazyWithRetry(() => import("./pages/VideoGeneratorPage"));
const VideoGuidePage = lazyWithRetry(() => import("./pages/VideoGuidePage"));
const ImageGuidePage = lazyWithRetry(() => import("./pages/ImageGuidePage"));
const AudioGeneratorPage = lazyWithRetry(() => import("./pages/AudioGeneratorPage"));
const VideoUpscalePage = lazyWithRetry(() => import("./pages/VideoUpscalePage"));
const EditPage = lazyWithRetry(() => import("./pages/EditPage"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const AdminDashboard = lazyWithRetry(() => import("./pages/AdminDashboard"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const DmcaPolicy = lazyWithRetry(() => import("./pages/DmcaPolicy"));
const RefundPolicy = lazyWithRetry(() => import("./pages/RefundPolicy"));
const PaymentSuccess = lazyWithRetry(() => import("./pages/PaymentSuccess"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));

const queryClient = new QueryClient();

// Loading fallback component - Premium Branding
const PageLoader = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-6">
    <div 
      className="animate-spin rounded-full h-12 w-12 border-b-2" 
      style={{ borderColor: 'hsl(var(--gold))' }} 
    />
    <span 
      className="text-sm font-medium animate-pulse bg-clip-text text-transparent"
      style={{
        backgroundImage: 'linear-gradient(135deg, hsl(45 100% 51%), hsl(0 72% 51%))'
      }}
    >
      Lovers Labs: World's Best Uncensored AI Generator
    </span>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <MetaPixelTracker />
        <AuthProvider>
          <CreditsProvider>
            <VideoPlaybackProvider>
              <ChunkErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                  <Route path="/admin" element={<ProtectedRoute><AdminRoute><AdminDashboard /></AdminRoute></ProtectedRoute>} />
                  <Route path="/generator" element={<ProtectedRoute><GeneratorPage /></ProtectedRoute>} />
                  <Route path="/video-generator" element={<ProtectedRoute><VideoGeneratorPage /></ProtectedRoute>} />
                  <Route path="/video-guide" element={<ProtectedRoute><VideoGuidePage /></ProtectedRoute>} />
                  <Route path="/image-guide" element={<ProtectedRoute><ImageGuidePage /></ProtectedRoute>} />
                  <Route path="/audio-generator" element={<ProtectedRoute><AdminRoute><AudioGeneratorPage /></AdminRoute></ProtectedRoute>} />
                  <Route path="/video-upscale" element={<ProtectedRoute><VideoUpscalePage /></ProtectedRoute>} />
                  <Route path="/edit" element={<ProtectedRoute><EditPage /></ProtectedRoute>} />
                  <Route path="/gallery" element={<Gallery />} />
                  <Route path="/payment-success" element={<PaymentSuccess />} />
                  <Route path="/termsofservice" element={<TermsOfService />} />
                  <Route path="/privacypolicy" element={<PrivacyPolicy />} />
                  <Route path="/dmcapolicy" element={<DmcaPolicy />} />
                  <Route path="/refund" element={<RefundPolicy />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              </ChunkErrorBoundary>
            </VideoPlaybackProvider>
          </CreditsProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
